import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { haptic } from '@/utils/haptic';
import { useSwipeGesture } from '@/hooks/use-swipe-gesture';

export interface SectionDef {
  label: string;
  accentColor: string;
}

export const SECTIONS: SectionDef[] = [
  { label: 'Wallpapers', accentColor: '#2dd4bf' },
  { label: 'Live', accentColor: '#2dd4bf' },
  { label: 'Ringtones', accentColor: '#2dd4bf' },
  { label: 'By Users', accentColor: '#2dd4bf' },
];

const GRADIENT_TEXT_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

// Horizontal gap between ribbon titles.
const GAP = 32;

// Slide glide timing — a slower, softly-decelerating easing curve applied
// symmetrically to every recenter animation, so every transition feels
// identical regardless of what triggered the step (tap, swipe, or an
// externally-driven activeIndex change).
const SLIDE_DURATION = 0.55;
const SLIDE_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

// Per-item opacity/blur crossfade — runs slightly faster than the slide so
// the fade/sharpen settles under the glide rather than snapping.
const FADE_DURATION = 0.5;

// Width of the left/right edge fade mask (px) — titles dissolve into the
// container edges instead of hard-clipping.
const EDGE_FADE = 56;

const mod = (n: number, m: number) => ((n % m) + m) % m;

interface SectionArrowSwitcherProps {
  activeIndex: number;
  /**
   * `delta` is the signed virtual-slot step just committed (see RibbonState
   * below) — positive glides right→left (forward, including the 3→0 wrap),
   * negative glides left→right (backward, including the 0→3 wrap). Passed
   * through unchanged so callers driving a parallel content-area transition
   * can match this exact direction, including for N-slot taps on a
   * non-adjacent title.
   */
  onIndexChange: (index: number, delta: number) => void;
  /**
   * When true, the ribbon is inert — no swipe/tap navigation (used for the
   * logged-out "Preview" lock, which keeps Explore pinned to Wallpapers).
   */
  locked?: boolean;
  /**
   * Overrides the displayed label of the centered/active title — used to
   * show "Preview" instead of the section name while locked/logged-out,
   * preserving the restricted-mode signal.
   */
  labelOverride?: string;
}

type RibbonItem = {
  key: number;
  iv: number;
  offset: number;
  sectionIdx: number;
  dist: number;
  opacity: number;
  blur: number;
  isActive: boolean;
  label: string;
};

interface RibbonState {
  // Continuous step counter — can move in either direction. Every committed
  // section change (tap, swipe left/right, or an externally-driven
  // activeIndex change) advances or retreats this by the gesture's signed
  // delta: +1/+N slides right→left (forward), -1/-N slides left→right
  // (backward). The sign of the delta is what the recenter animation reads
  // to decide which way to glide.
  virtualIndex: number;
  // Maps a virtual slot to a real section index: sectionIdx = mod(iv +
  // rotation, count). Updated atomically with virtualIndex on every step so
  // that the slot landing at the new center (virtualIndex) always resolves
  // to the section being navigated TO, while every other slot still reads
  // as a coherent natural cyclic order around it. Recomputing rotation
  // relative to the new virtualIndex is what makes negative deltas "just
  // work" — the ribbon renders a coherent order around the new center
  // regardless of which direction it moved to get there.
  rotation: number;
}

/**
 * Infinitely-looping horizontal "ribbon" section switcher rendered in place
 * of the Explore header's title text. The active section title is always
 * centered and rendered crisp; neighbors on either side are the real
 * adjacent sections, progressively faded/blurred by distance, and the list
 * wraps seamlessly in both directions (Wallpapers → Live → Ringtones →
 * By Users → Wallpapers ...). Swipe left to advance (next section enters
 * from the right), swipe right to go back (previous section enters from
 * the left), or tap any visible neighbor to jump straight to it in the
 * direction it sits.
 */
export const SectionArrowSwitcher: React.FC<SectionArrowSwitcherProps> = ({
  activeIndex,
  onIndexChange,
  locked = false,
  labelOverride,
}) => {
  const count = SECTIONS.length;
  const half = Math.max(4, count);

  // See RibbonState above — virtualIndex moves by the gesture's signed
  // delta, rotation maps virtual slots to real sections so the new center
  // slot always resolves to whichever section is being navigated to.
  const [ribbon, setRibbon] = useState<RibbonState>(() => ({ virtualIndex: activeIndex, rotation: 0 }));
  const { virtualIndex, rotation } = ribbon;
  const currentSectionIndex = mod(virtualIndex + rotation, count);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const mountedRef = useRef(false);
  const x = useMotionValue(0);

  // Shifts the ribbon (via the shared motion x value) so the active item's
  // measured center lands exactly on the container's center — robust to
  // titles of different widths since it's based on real measured rects.
  const recenter = useCallback((vIdx: number, animated: boolean) => {
    const container = containerRef.current;
    const activeEl = itemRefsMap.current.get(vIdx);
    if (!container || !activeEl) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const delta =
      (containerRect.left + containerRect.width / 2) -
      (activeRect.left + activeRect.width / 2);
    const targetX = x.get() + delta;
    if (animated) {
      animate(x, targetX, { duration: SLIDE_DURATION, ease: SLIDE_EASE });
    } else {
      x.set(targetX);
    }
  }, [x]);

  // Keep the local ribbon in sync with externally-driven activeIndex changes
  // (e.g. the content-area swipe gesture, or the logged-out/login reset to
  // Wallpapers). Steps by the signed shortest cyclic delta so the ribbon
  // glides in the same direction as whatever gesture drove the external
  // change (e.g. +1 for a forward wrap like By Users → Wallpapers, -1 for a
  // backward wrap like Wallpapers → By Users). If the ribbon already has
  // this section centered (including when activeIndex is simply echoing
  // back a change stepBy just emitted), this is a no-op — no double step.
  useLayoutEffect(() => {
    setRibbon((prev) => {
      const centered = mod(prev.virtualIndex + prev.rotation, count);
      if (centered === activeIndex) return prev;
      let delta = mod(activeIndex - centered, count);
      if (delta > count / 2) delta -= count;
      const newVirtualIndex = prev.virtualIndex + delta;
      const newRotation = mod(activeIndex - newVirtualIndex, count);
      return { virtualIndex: newVirtualIndex, rotation: newRotation };
    });
  }, [activeIndex, count]);

  // Re-center whenever the virtual position (or the active label's text,
  // which can change its measured width) changes. Instant on first mount,
  // animated afterwards.
  //
  // The very first `recenter` call on mount is a single synchronous
  // getBoundingClientRect() measurement, taken with no fallback if it lands
  // before the browser has finished compositing this ribbon's sticky,
  // backdrop-filtered header ancestor (see CategoryPageShell's top bar) —
  // reliable when this coincides with the app's very first paint, but a
  // real miss when the ribbon *remounts* into an already-stable, already
  // composited page (e.g. switching tabs away from Explore and back).
  // Nothing else re-triggers a recenter until the user changes section, so
  // a stale first measurement would otherwise leave the strip stuck at
  // x = 0 indefinitely — uncentered, with no crisp item in view. Re-measure
  // once more on the next frame to self-correct; this is a no-op snap when
  // the first measurement was already correct.
  useLayoutEffect(() => {
    const first = !mountedRef.current;
    mountedRef.current = true;
    recenter(virtualIndex, !first);
    if (first) {
      const raf = requestAnimationFrame(() => recenter(virtualIndex, false));
      return () => cancelAnimationFrame(raf);
    }
  }, [virtualIndex, rotation, labelOverride, recenter]);

  useLayoutEffect(() => {
    const onResize = () => recenter(virtualIndex, false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [virtualIndex, recenter]);

  // Commits a step of `delta` virtual slots, landing on targetSectionIndex.
  // virtualIndex moves by delta (positive or negative) and rotation is
  // recomputed relative to the new virtualIndex so slot (new virtualIndex)
  // resolves to targetSectionIndex. A positive delta glides right→left
  // (forward), a negative delta glides left→right (backward) — same
  // timing, mirrored direction.
  const stepBy = useCallback((delta: number, targetSectionIndex: number) => {
    if (locked) return;
    haptic.light();
    setRibbon((prev) => {
      const newVirtualIndex = prev.virtualIndex + delta;
      const newRotation = mod(targetSectionIndex - newVirtualIndex, count);
      return { virtualIndex: newVirtualIndex, rotation: newRotation };
    });
    onIndexChange(targetSectionIndex, delta);
  }, [locked, onIndexChange, count]);

  // Shared swipe gesture (see src/hooks/use-swipe-gesture.ts) — the same
  // axis-locked, 50px-threshold, discrete fire-on-release touch mechanics
  // used by the Explore page-body content swipe. Swiping left advances to
  // the next section with a +1 step (enters from the right); swiping right
  // goes to the previous section with a -1 step (enters from the left) —
  // the ribbon always glides in the same direction the finger swiped.
  const handleSwipeLeft = useCallback(() => {
    if (locked) return;
    stepBy(1, mod(currentSectionIndex + 1, count));
  }, [locked, stepBy, currentSectionIndex, count]);

  const handleSwipeRight = useCallback(() => {
    if (locked) return;
    stepBy(-1, mod(currentSectionIndex - 1, count));
  }, [locked, stepBy, currentSectionIndex, count]);

  const { swipeCallbackRef } = useSwipeGesture({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    enabled: !locked,
  });

  // Merge the swipe listener attachment with the existing measurement ref.
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    swipeCallbackRef(el);
  }, [swipeCallbackRef]);

  const items: RibbonItem[] = [];
  for (let offset = -half; offset <= half; offset++) {
    const iv = virtualIndex + offset;
    const sectionIdx = mod(iv + rotation, count);
    const section = SECTIONS[sectionIdx];
    const dist = Math.abs(offset);
    let opacity: number;
    let blur: number;
    if (dist === 0) { opacity = 1; blur = 0; }
    else if (dist === 1) { opacity = 0.55; blur = 1.2; }
    else if (dist === 2) { opacity = 0.28; blur = 2.2; }
    else { opacity = 0.18; blur = 3; }
    const isActive = offset === 0;
    items.push({
      key: iv,
      iv,
      offset,
      sectionIdx,
      dist,
      opacity,
      blur,
      isActive,
      label: isActive ? (labelOverride ?? section?.label ?? '') : (section?.label ?? ''),
    });
  }

  return (
    <div
      ref={setContainerRef}
      style={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height: 30,
        overflow: 'hidden',
        touchAction: 'pan-y',
        WebkitMaskImage: `linear-gradient(to right, transparent 0, #000 ${EDGE_FADE}px, #000 calc(100% - ${EDGE_FADE}px), transparent 100%)`,
        maskImage: `linear-gradient(to right, transparent 0, #000 ${EDGE_FADE}px, #000 calc(100% - ${EDGE_FADE}px), transparent 100%)`,
      }}
    >
      <motion.div
        style={{
          x,
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: GAP,
          willChange: 'transform',
          userSelect: 'none',
        }}
      >
        {items.map((item) => (
          <div
            key={item.key}
            ref={(el) => {
              if (el) itemRefsMap.current.set(item.iv, el);
              else itemRefsMap.current.delete(item.iv);
            }}
            onClick={() => {
              if (item.isActive || locked) return;
              // item.offset is the signed slot distance from center — use it
              // directly as the step delta so tapping a title slides in the
              // natural direction toward it (right neighbor → right→left
              // glide, left neighbor → left→right glide), traveling exactly
              // as many slots as the tapped title sits away.
              stepBy(item.offset, item.sectionIdx);
            }}
            role={item.isActive || locked ? undefined : 'button'}
            aria-label={item.isActive || locked ? undefined : `Switch to ${item.label}`}
            style={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              cursor: item.isActive || locked ? 'default' : 'pointer',
              pointerEvents: item.dist > 3 ? 'none' : 'auto',
              fontSize: 20,
              fontWeight: 800,
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '-0.02em',
              opacity: item.opacity,
              filter: item.blur > 0 ? `blur(${item.blur}px)` : 'none',
              transition: `opacity ${FADE_DURATION}s ease, filter ${FADE_DURATION}s ease`,
              ...GRADIENT_TEXT_STYLE,
            }}
          >
            {item.label}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default SectionArrowSwitcher;
